/* eslint-disable react/prop-types */
import React, { useState, useEffect, useContext, useRef } from "react"; //import the component part of React
import withRouter from "../WithRouter";
import { useNavigate, Navigate } from "react-router-dom";
import { isAuthenticatedCustomer } from "./services/CustomerAuth";
// Common
import StatusBlock from "../common/StatusBlock";
import FadeIn from "../common/FadeIn";
//Packages
import axios from "axios";
import { Col, Row, Container, Button, Form } from "react-bootstrap";
//Subcomponents
import ClassInfo from "./page-parts/ClassInfo";
import OtherClasses from "./page-parts/OtherClasses";
import CheckoutForm from "./page-parts/CheckoutForm";
import CouponCodeBlock from "./page-parts/CouponCodeBlock";
import StripeWrapper from "../common/StripeWrapper";
//Loading
import LoadingSpinner from "../LoadingSpinner";
//Context
import { BusinessContextConsumer } from "../BusinessContext";
//Analytics
import TagManager from "react-gtm-module";

const Checkout = (props) => {
  // State
  const [classData, setClassData] = useState("");
  const [otherClasses, setOtherClasses] = useState([]);
  const [prorationPrice, setProrationPrice] = useState("");
  const [selectedParticipant, setSelectedParticipant] = useState("");
  const [couponCode, setCouponCode] = useState("");
  // Get the classID and context.
  const classid = props.params.classid;
  const BusinessContext = useContext(BusinessContextConsumer);
  const { businessID, currencySymbol, currency } = BusinessContext;
  // ref the class ID
  const prevClassId = useRef(); //Reference the previous class ID to ensure new get request if it changes.
  // Get the class proration data if needed
  useEffect(async () => {
    prevClassId.current = classid; // Assign Refs Value to current load

    // Request the Class Data
    const classDataRequest = await axios.get(
      `/api/public_view/class_selected_plus/${businessID}/${classid}/`,
      {
        withCredentials: true,
      }
    );

    // Get the proration price only if necessary
    const returnedData = classDataRequest.data;
    if (returnedData.classData.Prorate && !returnedData.classData.monthStartBilling) {
      const prorationPriceRequest = await axios.get(
        `/api/public_view/class_selected/prorate/${businessID}/${classid}/`,
        {
          withCredentials: true,
        }
      );
      setProrationPrice(prorationPriceRequest.data.prorationPrice);
    }

    //Set the Datalayer Event
    TagManager.dataLayer({
      dataLayer: {
        event: "loggedInClassView",
        lessonType: returnedData.classData.LessonType,
      },
    });
    TagManager.dataLayer({
      dataLayer: {
        event: "view_item",
        ecommerce: {
          items: [
            {
              item_name: returnedData.classData.LessonType,
              price: returnedData.classData.Price,
              item_category: "subscription",
              quantity: 1,
            },
          ],
        },
      },
    });

    setSelectedParticipant(""); // Reloaded page so clear the selected Participant.
    setOtherClasses(returnedData.otherClasses);
    return setClassData(returnedData.classData);
  }, [classid]); // Run useEffect when the classID changes to get new class data.

  // Remove the interested class if there was one.
  if (sessionStorage.getItem("interestedClass") !== null) {
    sessionStorage.removeItem("interestedClass");
  }

  if (classData) {
    return (
      <FadeIn delay={250} duration={450}>
        <Container>
          {!isAuthenticatedCustomer() && <Navigate to='/login/' />}
          <ClassInfo
            prorationPrice={prorationPrice}
            classData={classData}
            currencySymbol={currencySymbol}
            direction="/"
          />
          {classData.MaxLearners > classData.Subscribers_Number ? (
            <SubscribersSelector
              classData={classData}
              setSelectedParticipant={setSelectedParticipant}
              currencySymbol={currencySymbol}
            />
          ) : (
            <WaitingRequest classData={classData} />
          )}
          {selectedParticipant !== "" && (
            <>
              <StripeWrapper>
                <CheckoutForm
                  classData={classData}
                  selectedParticipant={selectedParticipant}
                  currency={currency}
                  endpoint='general'
                  couponCode={couponCode}
                />
              </StripeWrapper>
              <CouponCodeBlock setCouponCode={setCouponCode} />
            </>
          )}
          <OtherClasses otherClasses={otherClasses} />
        </Container>
      </FadeIn>
    );
  } else {
    return <LoadingSpinner />;
  }
};

const SubscribersSelector = (props) => {
  const [eligibleSubscribers, setEligibleSubscribers] = useState([]);
  const class_id = props.classData._id;
  const prevClassId = useRef();
  useEffect(async () => {
    prevClassId.current = class_id;
    const getEligibleSubscribers = await axios.get(
      `/api/customer/customer_general/eligible_for_subscription/${class_id}`,
      {
        withCredentials: true,
      }
    );
    return setEligibleSubscribers(getEligibleSubscribers.data);
  }, [class_id]);

  if (eligibleSubscribers.length > 0) {
    return (
      <FadeIn delay={250} duration={450}>
        <Row>
          <Col sm='12'>
            <Form className='shadow mt-4 p-3 border'>
              <h3 className='mt-2 mb-2'>Subscribe</h3>
              <Form.Label>Choose your participant</Form.Label>
              <Form.Control
                as='select'
                value={props.selectedParticipant}
                onChange={(e) => props.setSelectedParticipant(e.target.value)}
                required
                custom
                className='border'
              >
                <option value='' defaultValue>
                  Select your option
                </option>
                {eligibleSubscribers.map((Participant) => (
                  <option key={Participant._id} value={Participant._id}>
                    {Participant.First_Name + " " + Participant.Last_Name}
                  </option>
                ))}
              </Form.Control>
              <Form.Text className='text-muted mt-2 mb-2'>
                The participant is the person you are subscribing to the class. It might be you, a
                child, or someone else you are responsible for. If you do not have a participant
                yet, you should set them up on your dashboard.{" "}
                <strong>
                  By entering your card details and clicking &quot;subscribe&quot; you will create a
                  recurring charge with stripe of{" "}
                  {props.currencySymbol + parseFloat(props.classData.Price).toFixed(2)} each month
                  as outlined in our terms and conditions.
                </strong>
              </Form.Text>
            </Form>
          </Col>
        </Row>
      </FadeIn>
    );
  } else {
    return (
      <Row>
        <Col sm='12' className='shadow mt-4 p-3 border text-center'>
          <div>
            <h3>No eligible subscribers...</h3>
          </div>
        </Col>
      </Row>
    );
  }
};

const WaitingRequest = (props) => {
  const [eligibleWaiters, setEligibleWaiters] = useState([]);
  const [selectedWaiter, setSelectedWaiter] = useState("");
  const class_id = props.classData._id;
  const prevClassId = useRef();
  // Errors and Submit
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [submitClicked, setSubmitClicked] = useState(false);
  // Utilities
  const navigate = useNavigate();

  useEffect(async () => {
    prevClassId.current = class_id;
    const getEligibleWaiters = await axios.get(
      `/api/customer/customer_general/eligible_for_waiting_list/${class_id}`,
      {
        withCredentials: true,
      }
    );
    return setEligibleWaiters(getEligibleWaiters.data);
  }, [class_id]);

  const joinWaitingList = async (e) => {
    e.preventDefault();
    setSubmitClicked(true);
    const data = {
      participant_id: selectedWaiter,
      class_id,
    };
    const requestWaitingSpace = await axios.post(
      "/api/customer/customer_general/waiting_list_add",
      data,
      {
        withCredentials: true,
      }
    );

    if (requestWaitingSpace.status === 200) {
      TagManager.dataLayer({
        dataLayer: {
          event: "joinedWaitingList",
          lessonType: props.classData.LessonType,
        },
      });
      return navigate("/success");
    } else {
      props.setSubmitClicked(false);
      return props.setErrorMessage("There was a problem with your request.");
    }
  };

  // Dismiss Message
  const dismissMessages = () => {
    setSuccessMessage("");
    return setErrorMessage("");
  };

  if (eligibleWaiters.length > 0) {
    return (
      <FadeIn delay={250} duration={450}>
        <Row className='mt-4'>
          <Col>
            <div className='shadow mt-4 p-3 border'>
              <h3 className='mt-4'>CLASS FULL: Join Waiting List</h3>
              <Form onSubmit={joinWaitingList}>
                <Form.Label>Choose your participant</Form.Label>
                <Form.Control
                  as='select'
                  value={selectedWaiter}
                  onChange={(e) => setSelectedWaiter(e.target.value)}
                  required
                  custom
                  className='border'
                >
                  <option value='' disabled defaultValue>
                    Select your option
                  </option>
                  {eligibleWaiters.map((Participant) => (
                    <option key={Participant._id} value={Participant._id}>
                      {Participant.First_Name + " " + Participant.Last_Name}
                    </option>
                  ))}
                </Form.Control>
                <Form.Text className='text-muted'>
                  The participant is the person you are adding to this classes waiting list. It
                  might be you, a child, or someone else you are responsible for. If you do not have
                  a participant yet you should set them up on your dashboard.
                </Form.Text>
                {selectedWaiter !== "" ? (
                  <Button
                    variant='success'
                    className='w-100 mt-3 mb-4'
                    type='submit'
                    disabled={submitClicked}
                  >
                    Join Waiting List
                  </Button>
                ) : (
                  <h4 className='mt-3'>You must select a participant to proceed.</h4>
                )}
                <div>
                  <StatusBlock
                    errorMessage={errorMessage}
                    successMessage={successMessage}
                    dismissMessages={dismissMessages}
                  />
                </div>
              </Form>
            </div>
          </Col>
        </Row>
      </FadeIn>
    );
  } else {
    return (
      <Row>
        <Col sm='12' className='shadow mt-4 p-3 border text-center'>
          <div>
            <h3>No eligible waiters...</h3>
          </div>
        </Col>
      </Row>
    );
  }
};

//Export
export default withRouter(Checkout);
